/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICookieContext } from '@msdyn365-commerce/core-internal/dist/types/interfaces/context/ICookieContext';
import { ProactiveChatNames } from '../../helpers/consts';
import { IChatContext, TContextProvider } from '../../../interfaces';
import { IPvaChatConnectorViewProps } from '../../msdyn365-pva-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * PageVisitTrigger class - proactive chat is triggered if user has visited current page for (config.proactiveOnPageVisitNumber) or more times
 */
class PageVisitTrigger extends ProactiveChatTrigger {
    public visits: number = 0;
    private cookieName: string = 'TimesPageVisited';
    private expdate: Date;
    private cookies: ICookieContext;

    constructor(props: IPvaChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.cookies = this.props.context.request.cookies;
        this.expdate = new Date();
        // Set expiry date to 365 days.
        this.expdate.setTime(this.expdate.getTime() + 24 * 60 * 60 * 1000 * 365);
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Cookie expiry date set to: '${this.expdate}'.`);

        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Cookie context is given: '${this.cookies.isConsentGiven()}'.`);
        if (this.cookies.isConsentGiven()) {
            this.cookies.setConsentCookie();
        }

        this._updateVisitCounter();
    }

    public async validate(): Promise<boolean> {
        let retVal: boolean = false;

        if (this.props.config.proactiveOnPageVisitNumber) {
            retVal = this.visits >= this.props.config.proactiveOnPageVisitNumber;
        }

        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Validation result: '${retVal}'.`);
        return retVal;
    }

    public clear(): void {
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Clearing cookie for page visit counter.`);
        this.cookies.set<number>(this.cookieName, 0, {
            domain: window.location.hostname,
            path: window.location.pathname,
            expires: this.expdate
        });
    }

    public getContextProvider(): TContextProvider {
        return () => {
            return {
                ...this.context,
                eCommerceCustIsProactiveChat: 'True',
                eCommerceCustProactiveType: ProactiveChatNames.PageVisits,
                eCommerceCustProactiveNumberOfVisits: `${this.visits}`
            };
        };
    }

    public getMessage(): string {
        // @TODO: Add message processing if needed
        return this.props.config.proactiveOnPageVisitMessage || this.props.config.proactiveDefaultMessage;
    }

    private _updateVisitCounter(): void {
        this.visits = this.cookies.get<number>(this.cookieName).value || 0;
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Page visit counter BEFORE update: '${this.visits}.`);
        ++this.visits;
        this.props.context.telemetry.debug(`[Chat][${PageVisitTrigger.name}] Page visit counter AFTER update: '${this.visits}.`);

        this.cookies.set<number>(this.cookieName, this.visits, {
            domain: window.location.hostname,
            path: window.location.pathname,
            expires: this.expdate
        });
    }
}

export default PageVisitTrigger;
